"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { StartIcon, StopSign } from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
  locale?: string
}

const MAX_ITEMS = 6

// Fires once when the element scrolls into view; used for the entrance animation.
function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  display: flex;
  justify-content: center;
  padding: 90px 120px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.down("lg")} {
    padding: 60px 40px;
  }

  ${(props) => props.theme.breakpoints.down("md")} {
    padding: 40px 20px;
  }
`

const ItemsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  max-width: 1200px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

/* Fixed card size per Figma: 155x244 mobile, 275x432 desktop (ASH-1403). */
const Card = styled.div<{ $inView: boolean; $index: number }>`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  aspect-ratio: 275 / 432;
  flex: 0 0 auto;
  width: 155px;

  /* slide-in-from-bottom entrance (fires once) */
  opacity: ${(props) => (props.$inView ? 1 : 0)};
  transform: translateY(${(props) => (props.$inView ? "0" : "24px")});
  transition: opacity 0.5s ease, transform 0.5s ease;
  transition-delay: ${(props) => props.$index * 0.08}s;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 275px;
  }
`

const MediaImage = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

/* dark scrim so overlaid text stays readable over the media */
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.75) 100%);
`

const CardContent = styled.div<{ $reserveControl?: boolean }>`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  padding: 16px;
  text-align: start;

  /* On desktop the play/pause button sits bottom (inline-end). Reserve space on that
     side for video cards so the text doesn't overlap the control (ASH-1570). */
  ${(props) => props.theme.breakpoints.up("md")} {
    ${({ $reserveControl }) => ($reserveControl ? "padding-inline-end: 56px;" : "")}
  }
`

/* mobile 24px/700, desktop 28px/500 (ASH-1409) */
const CardTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--inner-heading, var(--comp-heading));
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 28px;
    font-weight: 500;
  }
`

const TitleLine = styled.span`
  display: block;
`

const CardText = styled.p`
  /* hidden on mobile; only the bold title lines show. 18px on desktop (ASH-1409) */
  display: none;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--inner-text, var(--comp-text));
  margin: 8px 0 0;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: -webkit-box;
  }
`

const PlayPauseBtn = styled.button`
  position: absolute;
  top: 16px;
  inset-inline-end: 16px;
  background: white;
  border: none;
  border-radius: 40px;
  padding: 8px;
  opacity: 0.6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  z-index: 2;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px dashed var(--inner-heading, var(--comp-heading));
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    top: auto;
    bottom: 16px;
  }
`

// --- Types & parsing ---

type BenefitsImagesItem = {
  mediaUrl: string
  mediaAlt: string
  topLineTitle: string
  bottomLineTitle: string
  text: string
}

function isVideoUrl(url: string): boolean {
  return url.endsWith(".mp4") || url.endsWith(".webm")
}

function parseItem(block: any): BenefitsImagesItem {
  const media = extractProperty(block, "media", true) as any[] | undefined
  return {
    mediaUrl: media?.[0]?.url || "",
    mediaAlt: media?.[0]?.altText || "",
    topLineTitle: (extractProperty(block, "topLineTitle") as string) || "",
    bottomLineTitle: (extractProperty(block, "bottomLineTitle") as string) || "",
    text: (extractProperty(block, "text") as string) || "",
  }
}

function isEmptyItem(item: BenefitsImagesItem): boolean {
  return !item.mediaUrl && !item.topLineTitle && !item.bottomLineTitle && !item.text
}

// --- Sub-components ---

function BenefitsImageCard({
  item,
  isRtl,
  inView,
  index,
}: {
  item: BenefitsImagesItem
  isRtl: boolean
  inView: boolean
  index: number
}) {
  const analytics = useLandingAnalytics()
  const isVideo = isVideoUrl(item.mediaUrl)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)

  const togglePlayPause = () => {
    const video = videoRef.current
    if (!video) return
    if (isPlaying) {
      video.pause()
    } else {
      video.play()
    }
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.benefits)
    setIsPlaying(!isPlaying)
  }

  return (
    <Card $inView={inView} $index={index} role="listitem">
      {item.mediaUrl &&
        (isVideo ? (
          <MediaVideo
            ref={videoRef}
            src={getUmbracoMediaUrl(item.mediaUrl)}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <MediaImage src={getUmbracoMediaUrl(item.mediaUrl)} fill alt={item.mediaAlt} />
        ))}
      <Overlay />
      {isVideo && (
        <PlayPauseBtn
          onClick={togglePlayPause}
          aria-label={
            isPlaying
              ? isRtl
                ? "עצור תנועת הרכיב"
                : "Stop animation"
              : isRtl
              ? "הפעל תנועה רכיב"
              : "Play animation"
          }
        >
          {isPlaying ? <StopSign /> : <StartIcon />}
        </PlayPauseBtn>
      )}
      <CardContent $reserveControl={isVideo}>
        {(item.topLineTitle || item.bottomLineTitle) && (
          <CardTitle>
            {item.topLineTitle && <TitleLine>{item.topLineTitle}</TitleLine>}
            {item.bottomLineTitle && <TitleLine>{item.bottomLineTitle}</TitleLine>}
          </CardTitle>
        )}
        {item.text && <CardText>{item.text}</CardText>}
      </CardContent>
    </Card>
  )
}

// --- Main Component ---

function BenefitsImages({ data, locale }: Props) {
  const { ref, inView } = useInViewOnce<HTMLDivElement>()
  const itemBlocks = extractProperty(data, "benefitsImagesItemsBlockList") as any[] | ""
  const isRtl = locale === "he" || !locale
  const items = (Array.isArray(itemBlocks) ? itemBlocks : [])
    .map(parseItem)
    .filter((item) => !isEmptyItem(item))
    .slice(0, MAX_ITEMS)

  if (items.length === 0) return null

  return (
    <Section>
      <ItemsGrid ref={ref} role="list">
        {items.map((item, i) => (
          <BenefitsImageCard key={i} item={item} isRtl={isRtl} inView={inView} index={i} />
        ))}
      </ItemsGrid>
    </Section>
  )
}

export default BenefitsImages
